// INSTALL-1 CLAUSE 7 — **THE WAY BACK, IN HER MEMBER MENU.**
//
// Her order's own words: *"re-offerable from My Room settings"*, and her MENU SIZE order named where
// — *"the recent entries are kept but fitted: Have an issue?, SMS Notifications, **the re-offer
// button**"*. It lives beside `PushSettings` and `SmsSettings` for their own recorded reason: this
// menu is on every page, so the door exists on desktop and in the installed app alike without a
// roster of surfaces having to remember it.
//
// The LAW is `src/installPrompt.js` — `installReofferControl` decides which of the four states this
// is, and `askToInstallAgain` is the ONE declaration of how the sheet is asked to open. This file
// decides nothing (R28/R29).
import { useEffect, useState } from "react";
import { HomeIcon } from "lucide-react";
import { t } from "../strings.js";
import { installReofferControl, isStandalone, isDesktop, askToInstallAgain } from "../installPrompt.js";

export function InstallReofferView({ state, note, busy, language, onAsk }: {
  state: string; note: string; busy: boolean; language: string; onAsk: () => void;
}) {
  // >>> *WE HAVE NOT LOOKED YET* DRAWS NOTHING (R18/L5.1). <<< Read as *she has not installed it*,
  // this flashes an offer at a member standing in the installed app on every open of her own menu.
  //
  // >>> AND `hidden` DRAWS NOTHING FOR A DIFFERENT REASON, WHICH IS WHY IT IS ITS OWN STATE. <<<
  // Her clause 5: *"render MOBILE ONLY — desktop members never see it."* **It is not rendered rather
  // than hidden by a media query**, because a painted-out control still occupies the document, still
  // takes the tab order and still answers a screen reader — HOME-1's own argument for not painting
  // out the sidebar, one surface over.
  if (state === "unknown" || state === "hidden") return null;
  return (
    <div className="account-menu__block install-reoffer" data-install-reoffer={state}>
      <span className="account-menu__label">
        <HomeIcon size={14} /> {t(language, "install.reofferHeading")}
      </span>
      {state === "installed" ? (
        // A STATEMENT rather than a control that cannot change anything (A2's dead box, and ITEM A's
        // no-dead-read-only-lists clause one surface up).
        <p className="install-reoffer__done">{t(language, "install.reofferDone")}</p>
      ) : (
        <button
          type="button"
          className="button button--quiet install-reoffer__ask"
          disabled={busy}
          onClick={onAsk}
        >
          {t(language, "install.reofferAction")}
        </button>
      )}
      {note ? <p className="install-reoffer__note">{note}</p> : null}
    </div>
  );
}

export function InstallReoffer({ language = "en" }: { language?: string }) {
  const [known, setKnown] = useState(false);
  const [answer, setAnswer] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch("/api/install/state", { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (!live) return;
        if (!res.ok || !data?.ok) { setKnown(false); return; }
        setAnswer(data.answer || null);
        setKnown(true);
      } catch { if (live) setKnown(false); }
    })();
    return () => { live = false; };
  }, []);

  const ask = async () => {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      // >>> THE RESET IS AWAITED AND THE SHEET IS ONLY ASKED FOR AFTER IT LANDS (R52/R138). <<< The
      // reset is the DURABLE half — it is what lets the sheet come back on later landings — and the
      // announcement is the IMMEDIATE one. Announced first, a member who accepts the install has her
      // answer stamped and a slow reset then wipes it; and a reset that FAILED with the sheet opened
      // anyway spends one of her bounded three on an offer the store never un-stamped. Two facts,
      // and only one of them can be assumed (R18).
      const res = await fetch("/api/install/reset", { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      // R39/R47: these doors answer in the PAYLOAD, so the status code is never the answer.
      if (!res.ok || !data?.ok) { setNote(t(language, "install.reofferFailed")); return; }
      setAnswer(null);
      askToInstallAgain(window);
    } catch {
      setNote(t(language, "install.reofferFailed"));
    } finally { setBusy(false); }
  };

  return (
    <InstallReofferView
      /* >>> HER CLAUSE 5's MOBILE-ONLY HALF IS WIRED HERE AND DECIDED IN THE LAW. <<< The law
         defaults `desktop` to FALSE so a caller who forgets it errs toward SHOWING (R19), which
         means this one argument is the whole of the wiring and a clause holds it -- a law nothing
         calls is not a law (R28/R52/R138/R165). `isDesktop` is the house's ONE spelling of the
         question, whose own comment records that two spellings is one member refused by both. */
      state={installReofferControl({ standalone: isStandalone(window), known, answer, desktop: isDesktop(window) })}
      note={note}
      busy={busy}
      language={language}
      onAsk={() => void ask()}
    />
  );
}
